import { formatDateWithFns } from '@/helpers/helpers';
import { ELocalStorage } from '@/types/storage';
import { DateFormat } from '@/types/dateFormats';

export type TAuthEntryType = 'explicit_login' | 'carried_over';
export type TAuthMechanism = 'form' | 'refresh_token';

const buildDailyAuthStateKey = (userId: string, authDay: string) =>
  `${ELocalStorage.DAILY_AUTH_STATE_PREFIX}${userId}:${authDay}`;

const isAuthEntryType = (value: unknown): value is TAuthEntryType =>
  value === 'explicit_login' || value === 'carried_over';

export const getCurrentAuthDay = () =>
  formatDateWithFns(new Date(), DateFormat.YEAR_MONTH_DAY_NUMERIC);

export const readDailyAuthStateMarker = (
  userId: string,
  authDay: string,
): TAuthEntryType | null => {
  const stored = localStorage.getItem(buildDailyAuthStateKey(userId, authDay));
  return isAuthEntryType(stored) ? stored : null;
};

const pruneStaleDailyAuthStateMarkers = (currentAuthDay: string) => {
  const staleKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      key.startsWith(ELocalStorage.DAILY_AUTH_STATE_PREFIX) &&
      !key.endsWith(`:${currentAuthDay}`)
    ) {
      staleKeys.push(key);
    }
  }
  staleKeys.forEach(key => localStorage.removeItem(key));
};

export const writeDailyAuthStateMarker = (
  userId: string,
  authDay: string,
  authEntryType: TAuthEntryType,
) => {
  pruneStaleDailyAuthStateMarkers(authDay);
  localStorage.setItem(buildDailyAuthStateKey(userId, authDay), authEntryType);
};

export const shouldCaptureDailyAuthState = (
  existingMarker: TAuthEntryType | null,
  newAuthEntryType: TAuthEntryType,
): boolean => {
  if (existingMarker === 'explicit_login') return false;
  if (existingMarker === 'carried_over' && newAuthEntryType === 'carried_over') return false;
  return true;
};
